/**
 * RTWO Cryptography & Identity Engine — Address Derivation Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { AddressHex, AddressType, RTWOAddress } from '../domain-models';
import type {
  AddressDerivationRequest,
  AddressDerivationResult,
  IAddressDerivationService,
} from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import type { KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class AddressDerivationService
  extends BaseCryptoService
  implements IAddressDerivationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.ADDRESS_DERIVATION;
  readonly serviceName = 'Address Derivation Service';
  readonly description = 'Derives blockchain addresses from key pairs via the Key Provider.';
  readonly layer = 'R3' as const;
  readonly integrations = ['R4 Blockchain Protocol Engine', 'Key Provider'];
  readonly requiresKeyProvider = true;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories,
    private readonly keyProviderRegistry: KeyProviderRegistry
  ) {
    super(context);
  }

  async deriveAddress(request: AddressDerivationRequest): Promise<AddressDerivationResult> {
    const provider = this.keyProviderRegistry.getActiveProvider();
    const keyPair = this.repositories.keyPairs.findById(request.keyPairId);

    if (!keyPair || !this.keyProviderRegistry.isConfigured() || !provider.isReady()) {
      return {
        success: false,
        address: null,
        publicKeyHash: null,
        checksum: null,
        error: 'Key Provider not configured or not connected. No addresses derived.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    const derived = await provider.deriveAddress(
      request.keyPairId,
      request.addressType,
      request.derivationPath
    );

    if (!derived?.address) {
      return {
        success: false,
        address: null,
        publicKeyHash: null,
        checksum: null,
        error: 'Key Provider did not derive an address.',
        isPlaceholder: true,
        source: 'Key Provider — Operation Unavailable',
      };
    }

    const record: RTWOAddress = {
      address: derived.address,
      publicKeyHash: derived.publicKeyHash,
      addressType: request.addressType,
      derivationPath: request.derivationPath ?? null,
      checksum: derived.checksum,
      status: 'active',
      createdAt: Date.now(),
    };

    this.repositories.addresses.save(record);
    this.context.eventBus.emit(CryptoEventTypes.ADDRESS_DERIVED, derived.address, {
      keyPairId: request.keyPairId,
      addressType: request.addressType,
    });

    return {
      success: true,
      address: derived.address,
      publicKeyHash: derived.publicKeyHash,
      checksum: derived.checksum,
      error: null,
      source: 'Key Provider — Address Derived',
    };
  }

  getAddress(address: AddressHex): RTWOAddress | null {
    if (!this.keyProviderRegistry.getActiveProvider().isReady()) {
      return null;
    }

    return this.repositories.addresses.findByAddress(address);
  }

  getAddressesByType(addressType: AddressType): RTWOAddress[] {
    if (!this.keyProviderRegistry.getActiveProvider().isReady()) {
      return [];
    }

    return this.repositories.addresses.findByType(addressType);
  }
}
